import React from 'react';
import {
  BookingState,
  ICalendarEventType,
  PharmacistLegendVariant,
  ServiceTypes,
  TypeOfBill,
  acceptUpdatedTimings,
  cancelBooking,
  cancelRequestedBooking,
  counterOffer,
  pharmacistActions,
  pharmacistEvent,
  rejectUpdatedTimings,
} from '@pharmaplan/common';
import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';

import { FormikValues } from 'formik';
import useCancellationBooking from '../useCancellationBooking';
import { useAppSelector } from '../useAppSelector';
import { useAppDispatch } from '../useAppDispatch';
import BillDetails from '../../components/SelfService/BillDetails';
import { renderScreen, setCurrent } from '../../actions/drawerActions';
import { CancelContractType, ScreenTypes } from '../../helpers/Constants';
import classes from '../../components/SelfService/ServiceWorkshiftDetails/styles';
import strings from '../../localization';
import usePreRequest from '../usePreRequest';
import useServiceWorkshiftDetailsModals from './useServiceWorkshiftDetailsModals';
import { selectFooterPaginationCurrent } from '../../selectors/drawerSelector';
import { setDialog } from '../../reducers/dialogReducer';
import RequestContractModal from '../../components/SelfService/ServiceWorkshiftDetails/RequestContractModal';
import CancelContractRequestModal from '../../components/SelfService/ServiceWorkshiftDetails/CancelContractRequestModal';

const { None, UpdatedTiming } = BookingState;
const { requested, booked, workshift } = PharmacistLegendVariant;
const { counterOffer: counterOfferBill, finalBill } = TypeOfBill;
const { self, full } = ServiceTypes;

const { cancelButton, postedButton } = classes;

const useServiceWorkshiftDetails = (
  id: string,
  eventType: ICalendarEventType,
  isAutoSyncRecord?: boolean,
) => {
  const { handleSelfCancellation, showCancelConfirmation } = useCancellationBooking();
  const { preRequestBooking } = usePreRequest();
  const { showPostedList } = useServiceWorkshiftDetailsModals();

  const serviceType = ServiceHandler.getService();
  const dispatch = useAppDispatch();

  const event = useAppSelector(pharmacistEvent);
  const counterOfferEvent = useAppSelector(counterOffer);
  const current = useAppSelector(selectFooterPaginationCurrent);

  const {
    counterOfferId,
    bookingState,
    count = 0,
    longTermShiftContract,
  } = event ?? {};

  const isCounterOffer = counterOfferId;
  const isBooked = eventType === booked;
  const isWorkshift = eventType === workshift;
  const isLongTerm = !!longTermShiftContract?.longTermShiftContractId;

  const isSelfWorkshift = isWorkshift && serviceType === self;

  const billTypeConfig = isCounterOffer ? counterOfferBill : finalBill;
  const eventConfig = isCounterOffer ? counterOfferEvent : event;

  const successAction = pharmacistActions.getPharmacistEvent;

  const fullEvent = {
    ...event,
  };

  const handleFulLServiceCancellation = (values: FormikValues) => {
    const { reason } = values ?? {};
    dispatch(cancelBooking({ bookingid: id, reason }));
  };

  const selfServiceCancelBooking = () => {
    handleSelfCancellation(id, isAutoSyncRecord);
  };

  const fullServiceCancelBooking = () => {
    showCancelConfirmation(id, handleFulLServiceCancellation);
  };

  const disablePostedBtn = count === 0;

  const postedWorkShift = strings.formatString(
    strings.viewPostedWorkshifts,
    count,
  );

  const serviceConfig = {
    [self]: {
      cancelBooking: selfServiceCancelBooking,
      billDetails: (
        <BillDetails
          successAction={successAction}
          billType={billTypeConfig}
          event={eventConfig}
          isCounterOffer={!!isCounterOffer}
          isAutoSyncRecord={isAutoSyncRecord}
        />
      ),
    },
    [full]: {
      cancelBooking: fullServiceCancelBooking,
      billDetails: (
        <BillDetails
          successAction={successAction}
          billType={finalBill}
          event={fullEvent}
        />
      ),
    },
  };

  const { cancelBooking: handleCancelBooking } = serviceConfig[serviceType];

  const goToCounterOffer = (eventIds: Array<string>) => {
    dispatch(setCurrent(current));

    dispatch(
      renderScreen({
        eventIds,
        eventId: id,
        type: eventType,
        screenType: ScreenTypes.serviceWorkshiftDetails,
        screenNumber: 2,
      }),
    );
  };

  const handleCancelRequestBooking = () => {
    dispatch(cancelRequestedBooking({ id, isAutoSyncRecord }));
  };

  const handleAcceptUpdatedTimings = () => {
    dispatch(acceptUpdatedTimings(id));
  };

  const handleRejectUpdatedTimings = () => {
    dispatch(rejectUpdatedTimings(id));
  };

  const handlePreRequestBooking = () => {
    preRequestBooking(id, isAutoSyncRecord);
  };

  const openContractRequestModal = () => {
    dispatch(
      setDialog({
        heading: {
          title: strings.viewOfferAndRequestBooking,
        },
        Component: <RequestContractModal isAutoSyncRecord={isAutoSyncRecord} />,
        maxWidth: 'lg',
        showCloseButton: true,
      }),
    );
  };

  const openCancelContractRequestModal = () => {
    dispatch(
      setDialog({
        heading: {
          title: strings.viewOfferAndRequestBooking,
        },
        Component: (
          <CancelContractRequestModal
            successAction={pharmacistActions.getPharmacistEvent}
            eventType={CancelContractType.professionalDashboard}
          />
        ),
        maxWidth: 'lg',
        showCloseButton: true,
      }),
    );
  };

  const longTermButtons = () =>
    ({
      [workshift]: {
        primaryButton: {
          label: strings.viewOfferAndRequestBooking,
          onClick: openContractRequestModal,
        },
        secondaryButton: {
          label: '',
          onClick: null,
        },
      },
      [requested]: {
        primaryButton: {
          label: '',
          onClick: null,
        },
        secondaryButton: {
          label: strings.cancelRequest,
          onClick: openCancelContractRequestModal,
        },
      },
      [booked]: {
        secondaryButton: {
          label: strings.cancelBooking,
          customButtonStyle: cancelButton,
          onClick: handleCancelBooking,
        },
        primaryButton: { label: '', onClick: null },
      },
    });

  const bookingStateConfig = () => {
    const bookState = bookingState ?? None;

    const defaultButtons = {
      primaryButton: {
        label: postedWorkShift as string,
        onClick: showPostedList,
      },
      secondaryButton: {
        label: strings.cancelRequest,
        onClick: handleCancelRequestBooking,
      },
    };

    switch (bookState) {
      case None:
        if (isBooked) {
          return {
            secondaryButton: {
              label: strings.cancelBooking,
              customButtonStyle: cancelButton,
              onClick: handleCancelBooking,
            },
            primaryButton: { label: '', onClick: null },
          };
        }
        return {
          ...defaultButtons,
          primaryButton: {
            ...defaultButtons.primaryButton,
            variant: 'text' as const,
            disabled: disablePostedBtn,
            customButtonStyle: postedButton,
          },
        };

      case UpdatedTiming:
        if (isBooked) {
          return {
            primaryButton: {
              label: strings.approveUpdates,
              onClick: handleAcceptUpdatedTimings,
            },
            secondaryButton: {
              label: strings.declineUpdates,
              onClick: handleRejectUpdatedTimings,
            },
          };
        }
        return {
          primaryButton: { label: '', onClick: null },
          secondaryButton: {
            label: strings.declineUpdates,
            onClick: null,
          },
        };

      default:
        return defaultButtons;
    }
  };

  const buttonConfig = isLongTerm
    ? longTermButtons()
    : {
      [workshift]: {
        primaryButton: {
          label: strings.requestBooking,
          onClick: handlePreRequestBooking,
        },
        secondaryButton: {
          label: postedWorkShift as string,
          customButtonStyle: postedButton,
          disabled: disablePostedBtn,
          onClick: showPostedList,
        },
      },
      [requested]: bookingStateConfig(),
      [booked]: bookingStateConfig(),
    };

  return {
    serviceConfig,
    goToCounterOffer,
    buttonConfig,
    isCounterOffer,
    isBooked,
    isWorkshift,
    isSelfWorkshift,
    openContractRequestModal,
  };
};

export default useServiceWorkshiftDetails;
